/* Tour "forecast-ai" — met en avant la prévision par IA : la baguette magique
   « Générer une prévision IA pour la catégorie » (CashTable).

   ISOLATION TOTALE : on travaille sur une catégorie JETABLE « Exemple Okimia »
   (bac à sable, partagé avec forecast-objectives), créée au démarrage et supprimée
   à la fin. La suggestion IA est SIMULÉE (CashTable court-circuite l'appel back
   sur le bac à sable et présente une formule de démonstration). Le clone applique
   cette formule sur le bac à sable, qui part avec elle à la suppression. → aucune
   donnée réelle touchée, aucune dépendance à un historique / à « Non catégorisées ».

   La baguette n'apparaît normalement que sur une cellule « réalisée » : pendant le
   tuto on émet `tour:ai-magic` (CashTable révèle la baguette sur la 1ʳᵉ cellule de
   prévision du bac à sable, cf. tourRevealMagic + tourSandboxId). */

import AppBus from '@/bus/app-bus';
import {seedSandbox, cleanupSandbox, readSandbox} from './forecast-formulas';
import {isTourCompleted} from './onboarding-state';
import './types';

const MAGIC_ID = 'tour-ai-magic';

/** Sélecteur dynamique : la cellule de prévision du bac à sable porte l'ancre
 *  objectif-cell ET la baguette (révélée par tour:ai-magic). On cible la CELLULE
 *  (la baguette est en opacity:0 hors survol) ; `allowWithin:'.fi-magic'` ne
 *  laisse cliquer que l'étoile. */
function magicSelector(): string | null {
  const existing = document.getElementById(MAGIC_ID);
  if (existing && document.body.contains(existing)) return '#' + MAGIC_ID;
  const cell = document.querySelector('[data-tour-step="objectif-cell"]');
  if (!cell || !cell.querySelector('.magic-wraper')) return null;
  (cell as HTMLElement).id = MAGIC_ID;
  return '#' + MAGIC_ID;
}

/** Sélecteur du bouton Fx de la ligne bac à sable. Renvoie null tant que la
 *  pop-up de suggestion est ouverte (validation async) → l'étape Fx ne s'ancre
 *  qu'une fois la pop-up fermée. */
function fxSelector(): string | null {
  if (document.querySelector('.ai-suggestion-wrap')) return null;
  const ref = readSandbox();
  if (!ref) return null;
  const sel = `[data-tour-step="cat-fx-${ref.subId}"]`;
  return document.querySelector(sel) ? sel : null;
}

/** Force la vue tableau, marque + déplie le bac à sable et révèle la baguette IA
 *  (en rafale : la bascule de vue peut remonter CashTable après le 1er emit). */
function goToTableAndReveal() {
  AppBus.$emit('tour:dashboard-table');
  const ref = readSandbox();
  AppBus.$emit('tour:set-sandbox', ref?.subId);
  AppBus.$emit('tour:ai-magic', true);
  let tries = 0;
  const tick = () => {
    AppBus.$emit('tour:expand-category', ref?.parentId);
    AppBus.$emit('tour:set-sandbox', ref?.subId);
    AppBus.$emit('tour:ai-magic', true);
    if (++tries < 6) setTimeout(tick, 400);
  };
  tick();
}

window.FygrTour.register('forecast-ai', {
  title: 'Laisser l’IA proposer une prévision',
  page: '/dashboard',
  intro: {
    title: 'La prévision par IA',
    body: 'Okimia analyse l’historique d’une catégorie et vous propose automatiquement une formule de prévision. On le découvre sur une catégorie d’exemple.',
    note: 'Vous vous exercez sur une catégorie temporaire, supprimée automatiquement à la fin : vos données réelles ne sont pas modifiées.',
    startLabel: 'Commencer',
    optoutLabel: 'Désactiver les guides automatiques',
    optoutNote: 'Ils ne s’afficheront plus automatiquement. Vous pourrez les relancer à tout moment depuis « Tutoriels ».',
  },
  onStart: async () => {
    // Masque les icônes d'ajustement au survol (parasites derrière la pop-up).
    document.body.classList.add('tour-ai-active');
    // seedSandbox passe par la file de sérialisation : en enchaînement, il
    // attend la fin du cleanup du tuto précédent → on a toujours une catégorie
    // réelle (pas un résidu en cours de suppression).
    await seedSandbox();
    const ref = readSandbox();
    // Préchauffe la grille : la catégorie bac à sable vient d'être (re)créée,
    // mais la grille du dashboard vient d'un fetch séparé → sans reload,
    // « Exemple Okimia » n'apparaît pas et la baguette n'a pas de cible
    // (Target not found: null). On bascule en vue tableau + expand + reload.
    AppBus.$emit('tour:dashboard-table');
    AppBus.$emit('tour:set-sandbox', ref?.subId);
    AppBus.$emit('tour:expand-category', ref?.parentId);
    AppBus.$emit('tour:reload-table');
    AppBus.$emit('tour:ai-magic', true);
  },
  onExit: () => {
    document.body.classList.remove('tour-ai-active');
    AppBus.$emit('tour:ai-close');
    AppBus.$emit('tour:ai-magic', false);
    document.getElementById(MAGIC_ID)?.removeAttribute('id');
    // Supprime la catégorie d'exemple (la formule clonée part avec elle).
    cleanupSandbox();
  },
  steps: [
    {
      target: '[data-tour-step="dash-view-table"]',
      title: 'Passez en vue tableau',
      body: 'La prévision par IA se déclenche depuis la vue tableau. Cliquez sur l’icône tableau.',
      placement: 'bottom',
      clickToAdvance: true,
      nextDelay: 400,
      onNext: goToTableAndReveal,
    },
    {
      target: magicSelector,
      title: 'Générer une prévision IA',
      body: 'Sur la ligne « Exemple Okimia », cliquez sur l’icône IA : Okimia analyse votre historique et propose une formule de prévision pour la catégorie.',
      placement: 'left',
      allowWithin: '.fi-magic',
      // On attend le formulaire, pas seulement la pop-up : celle-ci s'affiche
      // immédiatement avec le préchargeur d'étalonnage, et l'étape suivante a
      // besoin que « Valider » (.validating) existe pour être cliquable.
      waitFor: '.ai-suggestion-wrap .validating',
      waitTimeout: 8000,
    },
    {
      target: '.ai-suggestion-wrap',
      title: 'La formule proposée',
      body: 'L’IA propose cette formule de prévision, prête à l’emploi. Cliquez sur « Valider » pour l’appliquer à la catégorie.',
      placement: 'left',
      clickToAdvance: true,
      allowWithin: '.validating',
      onNext: () => AppBus.$emit('tour:ai-close'),
      waitTimeout: 8000,
    },
    {
      target: fxSelector,
      title: 'Retrouvez vos formules ici',
      body: 'Le bouton Fx ouvre les formules de la catégorie. Vous pouvez y revenir à tout moment pour modifier la prévision, en ajouter d’autres, les désactiver ou les supprimer.',
      // Au-dessus du Fx : on ne recouvre pas le libellé de la catégorie.
      placement: 'top',
      lockTarget: true,
      clickToAdvance: false,
      waitTimeout: 8000,
    },
  ],
  celebration: {
    title: 'Prévision IA appliquée',
    body: 'Activez l’icône IA sur n’importe quelle catégorie disposant d’un historique pour obtenir une suggestion.',
    actionPanel: {
      title: 'Pour aller plus loin',
      body: 'Complétez votre prévisionnel.',
      actions: [
        {
          label: 'Intégrer vos factures',
          body: 'Projetez vos factures clients et fournisseurs.',
          icon: 'receipt_long',
          hideIf: () => isTourCompleted('invoices-overview'),
          href: '/invoices?tour=invoices-overview&from=recap',
        },
      ],
    },
  },
});

export {};
